import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import CopiableInput from "common/components/CopiableInput";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ApiKeyPermission } from "common/types";

import { API_KEYS_PERMISSIONS } from "../../../constants";
import useApiTokens from "../../../datahooks/useApiTokens";
import useApiTokensMutations from "../../../datahooks/useApiTokensMutations";
import { ApiKeyNameSchema } from "../../../schema";
import PermissionOption from "./PermissionOption";

export default function CreateApiTokenButton({ size }: { size: "sm" | "md" }) {
  const { tokens } = useApiTokens();
  const { createApiToken, isCreatingToken } = useApiTokensMutations();
  const isDesktop = useTwBreakpoint("lg");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    register,
    reset: resetForm,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
  }>({
    resolver: valibotResolver(ApiKeyNameSchema),
    defaultValues: tokens && {
      name: `API Key #${tokens.length + 1}`,
    },
  });

  const [selectedPermission, setSelectedPermission] =
    useState<ApiKeyPermission>("all");

  const [apiToken, setApiToken] = useState<string>(null);

  const permissionOptions = typedObjectEntries(API_KEYS_PERMISSIONS).map(
    ([role, { label, description }]) => ({
      label,
      value: role,
      description,
    }),
  );

  function resetStates(): void {
    resetForm({
      name: `API Key #${tokens.length + 1}`,
    });

    setSelectedPermission("all");
    setTimeout(() => setApiToken(null), 200);
  }

  useEffect(() => {
    if (tokens) {
      resetForm({ name: `API Key #${tokens.length + 1}` });
    }
  }, [tokens]);

  function onSubmit({ name }: { name: string }): void {
    const isWritable = selectedPermission === "all";

    createApiToken({
      name,
      write: isWritable,
    }).then((response) => {
      setApiToken(response.token);
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const content = apiToken ? (
    <>
      <Header title="Save your API key" />

      <Body className="items-center gap-6">
        <FormField
          label="Key"
          htmlFor="api-key"
          hint="Keep your API key safe and do not share it with third parties"
          className="w-full"
        >
          <CopiableInput id="api-key" value={apiToken} />
        </FormField>

        <div className="h-px w-full bg-black-100" />

        <div className="flex w-full flex-col gap-y-0.5">
          <span className="text-button-md">Permissions</span>

          <p className="text-black-500">
            {selectedPermission === "all" ? "Read and write" : "Read"} API
            resources
          </p>
        </div>
      </Body>

      <Footer className="justify-end">
        <Button onClick={() => setIsDialogOpen(false)}>Finish</Button>
      </Footer>
    </>
  ) : (
    <>
      <Header title="Create API key" />

      <form className="flex w-full flex-col" onSubmit={handleSubmit(onSubmit)}>
        <Body className="gap-4">
          <FormField
            label="Key name"
            htmlFor="name"
            error={errors.name?.message}
          >
            <Input
              {...register("name")}
              placeholder="Enter name"
              aria-label="Enter key name"
              id="name"
            />
          </FormField>

          <div className="h-px w-full bg-black-100" />

          <div className="flex flex-col gap-2">
            <span className="text-button-sm">Choose permissions:</span>

            {permissionOptions.map((option) => {
              const { label, value, description } = option;

              return (
                <PermissionOption
                  key={value}
                  option={value}
                  selectedPermission={selectedPermission}
                  onPermissionChange={setSelectedPermission}
                  label={label}
                  description={description}
                />
              );
            })}
          </div>
        </Body>

        <Footer className="justify-end">
          <Button isLoading={isCreatingToken} type="submit">
            Next
          </Button>
        </Footer>
      </form>
    </>
  );

  return (
    <Component
      open={isDialogOpen}
      onOpenChange={setIsDialogOpen}
      onAfterClose={resetStates}
    >
      <Trigger asChild>
        <Button leftIcon={<PlusCircle weight="fill" />} size={size}>
          Create API Key
        </Button>
      </Trigger>
      <Content size="lg">{content}</Content>
    </Component>
  );
}
